import React, { useState } from 'react';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { TabletButton } from '@campfire/tablet-button';
import { encodeDate, unpackToTime } from '@campfire/hot-date';
import { DateTime } from 'luxon';
import { gql } from '@apollo/client';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { useUser } from '../../../../global/auth/useUser';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { MyActivityCheckInDialog } from '../../vol-dashboard/vol-dashboard-my-elements/main-section/my-activities/CICO/MyActivityCheckInDialog';
import { MyActivityCheckOutDialog } from '../../vol-dashboard/vol-dashboard-my-elements/main-section/my-activities/CICO/MyActivityCheckOutDialog';
import UserCheckinIcon from '../../../../assets/icons/user-checkin.svg';
import UserCheckinLightIcon from '../../../../assets/icons/user-checkin-light.svg';

const GET_CICO_CARD_DATA = gql`
  query GetChatCICOCardData($activityId: String!, $activityDate: Date!, $volunteerId: String!) {
    vm {
      allowCICO
      activity(activityId: $activityId) {
        activityId
        name
        startTime
        endTime
        allowCICO
        hasOpenRoster
        activityLocation {
          placesAddress {
            description
          }
        }
        program {
          programId
          name
          allowCICO
        }
        sessions(includeOneOff: true) {
          sessionId
          name
        }
        publishedRoster(activityDate: $activityDate) {
          rosterings {
            publishedRosteringId
            volunteer {
              volunteerId
              isCheckedIn
              checkedIn {
                CICOId
                checkIn
                sessionReport {
                  activityReport {
                    activityDate
                    activity {
                      activityId
                    }
                  }
                  session {
                    sessionId
                  }
                }
              }
            }
            sessionRosterings {
              session {
                sessionId
                name
              }
            }
            dateRemoved
          }
        }
      }
      volunteer(volunteerId: $volunteerId) {
        volunteerId
        profile {
          profileId
          userId
          firstName
          lastName
          preferredName
          avatarUrl
        }
        CICOs {
          CICOId
          checkIn
          checkOut
          sessionReport {
            activityReport {
              activityDate
              activity {
                activityId
              }
            }
            session {
              sessionId
            }
          }
        }
      }
    }
  }
`;

interface ChatCICOCardProps {
  activityId: string;
  activityDate: string;
}

export const ChatCICOCard = ({ activityId, activityDate }: ChatCICOCardProps) => {
  const { getVolunteerIdentity } = useUser();
  const { volunteerId } = getVolunteerIdentity();
  const { theme } = useCampfireTheme();
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);

  const { data, loading, refetch } = useCampfireQuery<any, any>(GET_CICO_CARD_DATA, {
    options: { variables: { activityId, activityDate, volunteerId } },
  });

  const activity = data?.vm?.activity;
  const volunteer = data?.vm?.volunteer;
  const orgAllowCICO = data?.vm?.allowCICO;
  const rosteredColors = (theme.color.rosters.status as any).rostered;

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!activity) {
    return (
      <Box style={{ padding: 12, border: '1px solid #efefef', borderRadius: 8 }}>
        <Typography variant='body2' style={{ color: '#999' }}>
          Activity no longer available
        </Typography>
      </Box>
    );
  }

  const dt = DateTime.fromISO(activityDate);
  const nowDate = encodeDate(DateTime.local());
  const isToday = nowDate === activityDate;
  const location = activity.activityLocation?.placesAddress?.description;
  const grey = '#9E9E9E';

  const myRostering = activity.publishedRoster?.rosterings?.find(
    (r: any) => r.volunteer.volunteerId === volunteerId && !r.dateRemoved
  );
  const isRostered = !!myRostering;

  const cicoEnabled =
    (activity.allowCICO || activity.allowCICO === null) &&
    (activity.program?.allowCICO || activity.program?.allowCICO === null) &&
    orgAllowCICO;

  const isCheckedIn = myRostering?.volunteer?.isCheckedIn;
  const checkedInData = myRostering?.volunteer?.checkedIn;
  const checkedInActivityDate = checkedInData?.sessionReport?.activityReport?.activityDate;
  const checkedInActivityId = checkedInData?.sessionReport?.activityReport?.activity?.activityId;

  const isCheckedInHere = isCheckedIn && checkedInActivityDate === activityDate && checkedInActivityId === activityId;

  const volunteerCICOs = volunteer?.CICOs || [];
  const sessionRosterings = myRostering?.sessionRosterings || [];
  const isAttended =
    isRostered &&
    sessionRosterings.length > 0 &&
    sessionRosterings.every(
      (sr: any) =>
        sr?.session &&
        volunteerCICOs.find(
          (cico: any) =>
            cico.checkIn &&
            cico.checkOut &&
            cico.sessionReport?.activityReport?.activityDate === activityDate &&
            cico.sessionReport?.activityReport?.activity?.activityId === activityId &&
            cico.sessionReport?.session?.sessionId === sr.session.sessionId
        )
    );

  const checkInTimeRule = activity.startTime
    ? unpackToTime(activity.startTime)
        .diffNow()
        .as('hours') <= 6
    : true;
  const isAbleCheckIn = isToday && checkInTimeRule && isRostered && !isCheckedIn && !isAttended;
  const isAbleCheckOut = isRostered && isCheckedInHere && !isAttended;

  const isCheckInHighlight =
    activity.startTime &&
    unpackToTime(activity.startTime)
      .diffNow()
      .as('hours') >= 1;

  const notOnTeamColors = (theme.color.rosters.status as any)['not on team'];
  const panelColors = isRostered ? rosteredColors : notOnTeamColors;

  const activityForDialog: any = {
    __typename: 'VOLUNTEER_RecurringActivityType',
    activityId,
    name: activity.name,
    startTime: activity.startTime,
    endTime: activity.endTime,
    hasOpenRoster: activity.hasOpenRoster || false,
    activityRemoteLocation: null,
    activityLocation: activity.activityLocation,
    program: {
      __typename: 'VOLUNTEER_ProgramType',
      name: activity.program?.name || '',
      programId: activity.program?.programId || '',
    },
    sessions: (activity.sessions || []).map((s: any) => ({
      __typename: 'VOLUNTEER_SessionType',
      sessionId: s.sessionId,
      name: s.name,
      startTime: null,
      endTime: null,
      description: '',
      isFlexible: false,
      isOneOff: false,
      activityDate: null,
      currentFlexibleInstances: [],
      activityLocation: null,
      requiredRoles: [],
    })),
    activityDate,
    status: 'rostered',
    isEnrollable: false,
  };

  const volunteersForDialog: any[] = volunteer
    ? [
        {
          volunteerId: volunteer.volunteerId,
          profile: volunteer.profile || {
            profileId: '',
            userId: '',
            firstName: '',
            lastName: '',
            preferredName: '',
            avatarUrl: null,
          },
          isCheckedIn: !!isCheckedIn,
          sessionRosterings: sessionRosterings,
          CICOs: volunteerCICOs,
        },
      ]
    : [];

  let statusText = '';
  let statusColor = '#999';
  if (isAttended) {
    statusText = 'Attended';
    statusColor = rosteredColors.secondary;
  } else if (!isRostered) {
    statusText = 'Not Rostered';
  } else if (!isToday) {
    statusText = dt > DateTime.local() ? `Check-in on ${dt.toFormat('d LLL')}` : 'Past';
  } else if (!cicoEnabled) {
    statusText = 'Check-in disabled';
  } else if (!checkInTimeRule) {
    statusText = 'Check-in opens soon';
  }

  const cicoButton =
    cicoEnabled && isAbleCheckIn ? (
      <Box onClick={(e: React.MouseEvent) => e.stopPropagation()} style={{ marginLeft: 8 }}>
        <TabletButton
          variant={isCheckInHighlight ? 'outlined' : 'contained'}
          color='primary'
          size='small'
          onClick={() => setCheckInOpen(true)}
          startIcon={
            <img
              style={{ width: 12, height: 12, paddingLeft: 4 }}
              src={isCheckInHighlight ? UserCheckinIcon : UserCheckinLightIcon}
              alt=''
            />
          }
          style={{
            padding: '6px 12px',
            border: '1px solid #DCDCDC',
            whiteSpace: 'nowrap',
            textTransform: 'uppercase',
          }}
        >
          <Typography
            variant='caption'
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: isCheckInHighlight ? theme.color.secondary.main700 : theme.color.grey.neutralBrand100,
              textTransform: 'uppercase',
              lineHeight: '14px',
              marginTop: 1,
            }}
          >
            Check In
          </Typography>
        </TabletButton>
      </Box>
    ) : cicoEnabled && isAbleCheckOut ? (
      <Box onClick={(e: React.MouseEvent) => e.stopPropagation()} style={{ marginLeft: 8 }}>
        <TabletButton
          variant='contained'
          color='primary'
          size='small'
          onClick={() => setCheckOutOpen(true)}
          startIcon={<img style={{ width: 12, height: 12, paddingLeft: 4 }} src={UserCheckinLightIcon} alt='' />}
          style={{
            padding: '6px 12px',
            border: '1px solid #DCDCDC',
            whiteSpace: 'nowrap',
            textTransform: 'uppercase',
          }}
        >
          <Typography
            variant='caption'
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: theme.color.grey.neutralBrand100,
              textTransform: 'uppercase',
              lineHeight: '14px',
              marginTop: 1,
            }}
          >
            Check Out
          </Typography>
        </TabletButton>
      </Box>
    ) : statusText ? (
      <Typography
        variant='caption'
        style={{ color: statusColor, fontWeight: 600, marginLeft: 8, whiteSpace: 'nowrap' }}
      >
        {statusText}
      </Typography>
    ) : null;

  return (
    <>
      <Box style={{ width: 540, borderRadius: 8, border: '1px solid #efefef', overflow: 'hidden' }}>
        <Box display='flex' overflow='hidden'>
          <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            style={{ width: 80, minWidth: 80, background: panelColors.primary, borderRadius: '8px 0 0 8px' }}
          >
            <Typography
              style={{ fontWeight: 900, fontSize: 30, lineHeight: 1, color: panelColors.additional || '#fff' }}
            >
              {dt.day}
            </Typography>
            <Typography style={{ fontSize: 14, fontWeight: 700, color: panelColors.secondary, marginTop: 2 }}>
              {dt.monthShort}
            </Typography>
          </Box>
          <Box style={{ padding: '14px 18px', flex: 1, background: '#fff' }}>
            <Box display='flex' justifyContent='space-between' alignItems='center'>
              <Typography
                style={{ fontWeight: 800, fontSize: '1rem', color: '#545454', letterSpacing: '-0.02em', flex: 1 }}
                noWrap
              >
                {activity.name}
              </Typography>
              {cicoButton}
            </Box>

            <Box display='flex' flexWrap='wrap' alignItems='center' mt={0.5} style={{ gap: '8px' }}>
              {activity.startTime && activity.endTime && (
                <Box display='flex' alignItems='center'>
                  <svg
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke={grey}
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ marginRight: 6 }}
                  >
                    <path d='M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0' />
                    <path d='M12 7v5l3 3' />
                  </svg>
                  <Typography variant='caption' style={{ color: grey }}>
                    {DateTime.fromISO(`2000-01-01T${activity.startTime}`).toFormat('h:mm a')} -{' '}
                    {DateTime.fromISO(`2000-01-01T${activity.endTime}`).toFormat('h:mm a')}
                  </Typography>
                </Box>
              )}
              {location && (
                <Box display='flex' alignItems='center'>
                  <svg
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke={grey}
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ marginRight: 6 }}
                  >
                    <path d='M12 11m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0' />
                    <path d='M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z' />
                  </svg>
                  <Typography variant='caption' style={{ color: grey }} noWrap>
                    {location}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <MyActivityCheckInDialog
        open={checkInOpen}
        type='myself'
        onClose={() => setCheckInOpen(false)}
        handleSuccess={() => {
          if (refetch) refetch();
        }}
        activity={activityForDialog}
        volunteers={volunteersForDialog}
      />

      <MyActivityCheckOutDialog
        open={checkOutOpen}
        type='myself'
        onClose={() => setCheckOutOpen(false)}
        handleSuccess={() => {
          if (refetch) refetch();
        }}
        activity={activityForDialog}
      />
    </>
  );
};
